import { dropTask } from 'ember-concurrency';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import { on } from '@ember/modifier';
import { Button } from 'tio-ui/components/buttons';
import { PhFileArrowDown } from 'ember-phosphor-icons';

interface TioDownloadButtonSignature {
  Args: {
    filename: string;
    url?: string;
    needsUrl?: (passthru?: unknown) => Promise<string>;
  };
  Blocks: {
    default: [
      {
        downloadFile: unknown;
        isRunning: boolean;
      },
    ];
  };
  Element: HTMLElement;
}

export default class TioDownloadButtonComponent extends Component<TioDownloadButtonSignature> {
  @service declare store: Store;

  get filename() {
    return this.args.filename || 'tuition';
  }

  async needsUrl(passthru?: unknown) {
    return await this.args.needsUrl?.(passthru);
  }

  download = dropTask(async (event) => {
    let url = this.args.url;
    const { filename } = this;

    if (!url && this.args.needsUrl) {
      url = await this.needsUrl();
      if (!url) {
        return;
      }
    } else if (!url) {
      return;
    }
    const adapter = this.store.adapterFor('application');
    const headers = adapter.headers;

    const response = await fetch(url, { headers: headers });

    if (response.ok) {
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = fileUrl;
      tempLink.setAttribute('download', filename);

      if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
      }

      const parent = event.target.parentElement;
      parent.appendChild(tempLink);
      tempLink.click();
      parent.removeChild(tempLink);
    } else {
      console.error('Unable to download file due to an error.', response);
    }
  });

  <template>
    {{#if (has-block)}}
      {{yield (hash downloadFile=this.download.perform isRunning=this.download.isRunning)}}
    {{else}}
      <Button
        {{on "click" this.download.perform}}
        @intent="primary"
        @appearance="outlined"
        @isRunning={{this.download.isRunning}}
        ...attributes
      >
        <PhFileArrowDown @size="24" />
      </Button>
    {{/if}}
  </template>
}
